import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import uuid from 'uuid/v1';
import { Bert } from 'meteor/themeteorchef:bert';
import FileSaver from 'file-saver';

import { Stack, Box, Card, Button, CardMedia, Typography, Tooltip } from '@mui/material';
import { isEmpty, find, isEqual, map, filter, cloneDeep } from 'lodash';
import Styles from '../styles';
import {
  renderButton,
  renderIconButton,
  renderSelect,
  renderSwitch,
  renderTable,
  renderTextField
} from '../../MaterialMUIComponent';
import TablePagination from '../../TablePagination';

import UploadFileComponent from '../../UploadFileComponent/UploadFileComponent';
import ResponsivePlayer from '../../ResponsivePlayer';
import OpenModalFromUrl from '../../OpenModalFromUrl';

import * as templateOps from '../../../../api/AnnotationTemplates/annotation-template-operations';
import { ELEMENT_TYPES } from '../../../../api/AnnotationTemplates/AnnotationTemplates';

const IMG_DEFAULT = 'https://sd1.d3.ipsilum.com/files_uploaded/geomotionvideo.png';

const TemplateMultipleItem = ({
  elementItem,
  updateAnnotationFields,
  isAdvancedMode,
  previewMode,
  viewBasicEditMultis
}) => {
  const isMultiVideo = isEqual(
    templateOps.getElementType(elementItem),
    ELEMENT_TYPES.MULTIPLEVIDEOS
  );
  const isMultiPicture = isEqual(
    templateOps.getElementType(elementItem),
    ELEMENT_TYPES.MULTIPLEPICTURES
  );
  const isMultiIframe = isEqual(
    templateOps.getElementType(elementItem),
    ELEMENT_TYPES.MULTIPLEIFRAME
  );

  const showFileCaption = isMultiIframe || isMultiVideo;

  const multiValuesSchema = templateOps.getElementMultiValues(elementItem);
  const schemaDefault = {
    name: '',
    height: '300',
    width: '100',
    imgLink: IMG_DEFAULT
  };
  const [newSchema, setNewSchema] = React.useState(schemaDefault);

  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 7;
  const totalCount = multiValuesSchema.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const startItemsToShow = filter(multiValuesSchema, (element, index) => index < itemsPerPage);
  const [multiSchemaToShow, setMultiSchemaToShow] = React.useState(startItemsToShow);
  const [itemSelected, setItemSelected] = React.useState({});

  const [openFullScreen, setOpenFullScreen] = React.useState(false);

  const indexMultiItem = schema =>
    multiValuesSchema.indexOf(find(multiValuesSchema, item => isEqual(item.url, schema.url)));

  const changePage = pageNumber => {
    const indexFilter = itemsPerPage * pageNumber;
    const prevIndexFilter = itemsPerPage * (pageNumber - 1);
    const newDatasToShow = filter(
      multiValuesSchema,
      (element, index) => index >= prevIndexFilter && index < indexFilter
    );
    setMultiSchemaToShow(newDatasToShow);
    setCurrentPage(pageNumber);
  };

  const addMapList = (fastSchema = {}) => {
    let newMultiSchema = [];
    const cloneMultiSchema = cloneDeep(multiValuesSchema);
    const cloneNewSchema = isEmpty(fastSchema) ? cloneDeep(newSchema) : cloneDeep(fastSchema);
    if (!isEqual(indexMultiItem(cloneNewSchema), -1)) {
      cloneMultiSchema.splice(indexMultiItem(cloneNewSchema), 1, cloneNewSchema);
      newMultiSchema = cloneDeep(cloneMultiSchema);
    } else newMultiSchema = [...multiValuesSchema, cloneNewSchema];

    if (!isEmpty(fastSchema))
      setMultiSchemaToShow(filter(newMultiSchema, (element, index) => index < itemsPerPage));
    updateAnnotationFields(elementItem, 'multiValues', newMultiSchema);
    Bert.alert('MultiItem add/update successfuly', 'success');
  };

  const deleteMapList = () => {
    const cloneMultiSchema = cloneDeep(multiValuesSchema);
    if (!isEqual(indexMultiItem(newSchema), -1))
      cloneMultiSchema.splice(indexMultiItem(newSchema), 1);
    updateAnnotationFields(elementItem, 'multiValues', cloneMultiSchema);
    Bert.alert('MultiItem remove successfuly', 'warning');
  };

  const handleSchemaList = nameSchema => {
    if (isEqual(nameSchema, 'New...')) setNewSchema(schemaDefault);
    else {
      const schema = find(multiValuesSchema, item => isEqual(item.name, nameSchema));
      setNewSchema(schema);
    }
  };

  const handleViewMultiItem = item => {
    if (isEqual(item.name, itemSelected.name) && !item.onlyFullScreen) setItemSelected({});
    else setItemSelected(item);
    if (item.onlyFullScreen) setOpenFullScreen(true);
  };

  const downloadFromUrl = async (fileURL, nameFile) => {
    FileSaver.saveAs(fileURL, nameFile);
  };

  React.useEffect(() => {
    const multiValues = templateOps.getElementMultiValues(elementItem);
    const itemsToShow = filter(multiValues, (element, index) => index < itemsPerPage);
    setCurrentPage(1);
    setMultiSchemaToShow(itemsToShow);
    setItemSelected({});
  }, [elementItem]);

  const iconsPosition = [
    {
      functionProp: () => updateAnnotationFields(elementItem, 'positionElement', 'flex-start'),
      icon: <FontAwesomeIcon icon="align-left" />,
      title: 'Align left',
      disabled: false,
      color: templateOps.getElemenPosition(elementItem) === 'flex-start' ? 'green' : 'blue'
    },
    {
      functionProp: () => updateAnnotationFields(elementItem, 'positionElement', 'center'),
      icon: <FontAwesomeIcon icon="align-justify" />,
      title: 'Center',
      disabled: false,
      color: templateOps.getElemenPosition(elementItem) === 'center' ? 'green' : 'blue'
    },
    {
      functionProp: () => updateAnnotationFields(elementItem, 'positionElement', 'flex-end'),
      icon: <FontAwesomeIcon icon="align-right" />,
      title: 'Align right',
      disabled: false,
      color: templateOps.getElemenPosition(elementItem) === 'flex-end' ? 'green' : 'blue'
    }
  ];

  const buttonsSaveRemove = [
    {
      functionProp: () => addMapList(),
      contentProp: !isEmpty(find(multiValuesSchema, item => isEqual(item.url, newSchema.url)))
        ? 'Update '
        : 'Add',
      disabledProp: isEmpty(newSchema.url),
      colorProp: 'success',
      styleProp: {}
    },
    {
      functionProp: () => deleteMapList(),
      contentProp: 'Remove',
      disabledProp: isEmpty(find(multiValuesSchema, item => isEqual(item.url, newSchema.url))),
      colorProp: 'error',
      styleProp: {}
    }
  ];

  const buttonsIframe = map(
    [
      {
        functionProp: () => setOpenFullScreen(true),
        icon: 'expand',
        title: 'Full screen',
        isShow: !itemSelected.onlyFullScreen && templateOps.getEnabledFullScreen(elementItem)
      },
      {
        functionProp: () => downloadFromUrl(itemSelected.url, itemSelected.name),
        icon: 'download',
        title: 'Download File',
        isShow: !isMultiVideo && !itemSelected.onlyFullScreen && itemSelected.isDownload
      }
    ],
    (iconElement, index) =>
      iconElement.isShow &&
      renderIconButton({
        key: `iconButton-${templateOps.getElementOrder(elementItem)}-${index}`,
        onClick: iconElement.functionProp,
        icon: <FontAwesomeIcon icon={iconElement.icon} />,
        titleTooltip: iconElement.title,
        disabled: iconElement.disabled
      })
  );

  const renderTooltip = () => {
    return (
      <Stack width="100%" direction="row">
        <Typography marginRight="5%">{templateOps.getElementCaption(elementItem)}</Typography>
        {buttonsIframe}
      </Stack>
    );
  };

  const elementsTablePreview = [
    {
      row: 1,
      column: 0,
      rowJustifyContent: templateOps.getElemenPosition(elementItem),
      isShowRow: true,
      showElement: true,
      elements: [
        multiSchemaToShow.map((item, index) => {
          return (
            <Tooltip
              key={`tooltip-${templateOps.getElementOrder(elementItem)}-${index + 1}`}
              title={item.name}
            >
              <Card
                key={`card-${templateOps.getElementOrder(elementItem)}-${index + 1}`}
                style={{ height: '120px', width: '120px', marginRight: '2%' }}
              >
                <Button
                  key={`button-${templateOps.getElementOrder(elementItem)}-${index + 1}`}
                  style={{ padding: 0 }}
                  onClick={() => handleViewMultiItem(item)}
                >
                  <CardMedia
                    key={`cardMedia-${templateOps.getElementOrder(elementItem)}-${index + 1}`}
                    component="img"
                    style={{ height: '100%' }}
                    image={isMultiPicture ? item.url : item.imgLink}
                  />
                </Button>
              </Card>
            </Tooltip>
          );
        })
      ]
    },
    {
      row: 2,
      column: 0,
      rowJustifyContent: 'flex-end',
      isShowRow: true,
      showElement: !isEmpty(multiValuesSchema),
      elements: [
        totalPages > 1 && (
          <Typography
            marginTop="1%"
            marginRight="1%"
          >{`${currentPage} - ${totalPages}`}</Typography>
        ),
        <TablePagination
          key={uuid()}
          currentPage={currentPage}
          isPaginationOn
          itemsPerPage={itemsPerPage}
          stretchView
          totalCount={totalCount}
          changePage={changePage}
        />
      ]
    }
  ];

  const elementsTableEdit = [
    {
      row: 1,
      column: 0,
      isShowRow: true,
      isShowCol: true,
      showElement: true,
      rowJustifyContent: 'flex-start',
      elements: [
        renderSwitch({
          key: `enabledFullScreen- ${templateOps.getElementOrder(elementItem)}`,
          checked: templateOps.getEnabledFullScreen(elementItem),
          onChange: () => {
            updateAnnotationFields(
              elementItem,
              'enabledFullScreen',
              !templateOps.getEnabledFullScreen(elementItem)
            );
          },
          labelCheck: 'Enabled full screen',
          labelUncheck: 'Disabled full screen'
        }),
        renderSwitch({
          key: `fullScreen- ${templateOps.getElementOrder(elementItem)}`,
          checked: newSchema.onlyFullScreen,
          onChange: event => setNewSchema({ ...newSchema, onlyFullScreen: event.target.checked }),
          labelCheck: 'Full Screen',
          labelUncheck: 'Inside Annotation'
        }),
        isMultiPicture &&
          renderSwitch({
            key: `download-${templateOps.getElementOrder(elementItem)}`,
            checked: newSchema.isDownload,
            onChange: () =>
              setNewSchema({
                ...newSchema,
                isDownload: !newSchema.isDownload
              }),
            labelCheck: 'Enabled download',
            labelUncheck: 'Disabled download'
          }),
        map(iconsPosition, (iconElement, index) =>
          renderIconButton({
            key: `iconButton-${templateOps.getElementOrder(elementItem)}-${index}`,
            style: { color: iconElement.color },
            onClick: iconElement.functionProp,
            icon: iconElement.icon,
            titleTooltip: iconElement.title,
            disabled: iconElement.disabled
          })
        )
      ]
    },
    {
      row: 2,
      column: 1,
      isShowRow: true,
      isShowCol: true,
      colWidth: '100vh',
      showElement: true,
      elements: [
        <Box
          key={`box-row2-column1-texfields-${templateOps.getElementOrder(elementItem)} `}
          width="95%"
        >
          {renderTextField({
            key: `textfield-${templateOps.getElementOrder(elementItem)}-name`,
            label: 'name',
            style: { width: '100%', marginTop: '2%', marginBottom: '2%' },
            moreProps: {
              fullWidth: true,
              name: `${templateOps.getElementKey(elementItem)}`,
              onChange: event => {
                setNewSchema({ ...newSchema, name: event.target.value });
              },
              value: newSchema.name,
              sx: {
                '& legend': { display: 'none' },
                '& fieldset': { top: 0 }
              }
            }
          })}
          <Stack direction="row" display="flex" width="100%">
            {!isMultiVideo &&
              renderTextField({
                type: 'number',
                label: 'Height',
                key: `textfield-${templateOps.getElementOrder(elementItem)}-height`,
                style: { width: '100%', marginTop: '2%', marginBottom: '2%' },
                moreProps: {
                  onChange: event => setNewSchema({ ...newSchema, height: event.target.value }),
                  value: newSchema.height,
                  sx: {
                    '& legend': { display: 'none' },
                    '& fieldset': { top: 0 }
                  }
                }
              })}
            {isMultiIframe &&
              renderTextField({
                type: 'number',
                label: 'Width',
                key: `textfield-${templateOps.getElementOrder(elementItem)}-width`,
                style: { width: '100%', marginTop: '2%', marginBottom: '2%' },
                moreProps: {
                  onChange: event => setNewSchema({ ...newSchema, width: event.target.value }),
                  value: newSchema.width,
                  sx: {
                    '& legend': { display: 'none' },
                    '& fieldset': { top: 0 }
                  }
                }
              })}
          </Stack>
        </Box>,
        <UploadFileComponent
          key={`${templateOps.getElementOrder(elementItem)}-image`}
          labelProp="File image"
          linkValueProp={isMultiPicture ? newSchema.url : newSchema.imgLink}
          handleValueLink={link => {
            if (isMultiPicture) setNewSchema({ ...newSchema, url: link });
            else setNewSchema({ ...newSchema, imgLink: link });
          }}
        />,
        showFileCaption && (
          <UploadFileComponent
            key={`${templateOps.getElementOrder(elementItem)}-link`}
            labelProp={isMultiIframe ? 'Link iframe' : 'Link video'}
            linkValueProp={newSchema.url}
            handleValueLink={link => {
              setNewSchema({ ...newSchema, url: link, imgLink: newSchema.imgLink || IMG_DEFAULT });
            }}
          />
        )
      ]
    },

    {
      row: 2,
      column: 2,
      isShowRow: true,
      isShowCol: true,
      showElement: true,
      elements: [
        renderSelect({
          key: `select-multi-${templateOps.getElementOrder(elementItem)}`,
          multiple: true,
          style: {
            width: '95%',
            marginTop: '1/2%'
          },
          label: 'Item',
          inputLabel: 'Item',
          arrayOptions: ['New...'].concat(map(multiValuesSchema, item => item.name)),
          onChange: event => handleSchemaList(event.target.value),
          value: !isEmpty(find(multiValuesSchema, item => isEqual(item.url, newSchema.url)))
            ? [newSchema.name]
            : ['New...']
        }),
        <Stack
          key={`row2-column2-buttonsRemove-${templateOps.getElementOrder(elementItem)}-`}
          direction="row"
          display="flex"
          justifyContent="flex-end"
          marginTop="2%"
          marginRight="2.5%"
        >
          {map(buttonsSaveRemove, (item, index) =>
            renderButton({
              key: `button-remove-${templateOps.getElementOrder(elementItem)}-${index}`,
              onClick: item.functionProp,
              contentButton: item.contentProp,
              disabled: item.disabledProp,
              color: item.colorProp,
              style: item.styleProp
            })
          )}
        </Stack>
      ]
    }
  ];

  const videoComponentFullScreen = () => {
    if (openFullScreen) {
      return (
        <ResponsivePlayer
          attributes={{
            muted: true
          }}
          showControls
          url={itemSelected.url}
          heightDisplay="90%"
          isPreviewMapEditor
          playing={false}
        />
      );
    }
    return '';
  };

  return (
    <>
      {!isAdvancedMode && (
        <>
          {isEqual(previewMode, 'edition') && viewBasicEditMultis && (
            <UploadFileComponent
              key={`${templateOps.getElementOrder(elementItem)}-basicEdit`}
              labelProp="Upload file"
              linkValueProp={newSchema.url}
              handleValueLink={link => {
                setNewSchema({
                  ...newSchema,
                  imgLink: isMultiPicture ? link : IMG_DEFAULT,
                  name: `${templateOps.getElementType(elementItem)}-${multiValuesSchema.length +
                    1}`,
                  url: link
                });
                addMapList({
                  ...newSchema,
                  imgLink: isMultiPicture ? link : IMG_DEFAULT,
                  name: `${templateOps.getElementType(elementItem)}-${multiValuesSchema.length +
                    1}`,
                  url: link
                });
              }}
            />
          )}
          {renderTable(elementsTablePreview)}
          {!isEmpty(itemSelected) && (
            <Box marginLeft="2%" marginTop="5%">
              <Stack direction="row" display="flex" justifyContent="flex-start">
                <OpenModalFromUrl
                  isFullScreen
                  isImage={isMultiPicture}
                  isLoading={!isMultiPicture}
                  openModal={openFullScreen}
                  setOpenModal={setOpenFullScreen}
                  url={isMultiVideo ? '' : itemSelected.url}
                  title={itemSelected.name}
                  isComponent={isMultiVideo}
                  component={videoComponentFullScreen}
                  idComponent="video-fullScreen"
                />
              </Stack>

              {isMultiPicture && !itemSelected.onlyFullScreen && (
                <Styles.LightTooltip
                  key={`tooltip-picture-${itemSelected.url}`}
                  title={renderTooltip()}
                  placement="top-start"
                  style={{
                    display: 'flex',
                    justifyContent: templateOps.getElemenPosition(elementItem),
                    height: `${itemSelected.height}px`,
                    width: `${itemSelected.height}px`
                  }}
                >
                  <Card width="100%" height="100%">
                    <img
                      key={`tooltip-picture-${itemSelected.url}`}
                      src={itemSelected.url}
                      alt="imagen"
                      width={`${itemSelected.height}px`}
                      height={`${itemSelected.height}px`}
                    />
                  </Card>
                </Styles.LightTooltip>
              )}
              {isMultiVideo && !itemSelected.onlyFullScreen && (
                <Styles.LightTooltip
                  key={`tooltip-video-${itemSelected.url}`}
                  title={renderTooltip()}
                  placement="top-start"
                  style={{
                    display: 'flex',
                    justifyContent: templateOps.getElemenPosition(elementItem),
                    height: '100%',
                    width: '100%'
                  }}
                >
                  <ResponsivePlayer
                    key={`videoItem-${itemSelected.url}`}
                    url={itemSelected.url}
                    showControls={false}
                  />
                </Styles.LightTooltip>
              )}
              {isMultiIframe && !itemSelected.onlyFullScreen && (
                <Styles.LightTooltip
                  key={`tooltip-iframe-${itemSelected.url}`}
                  title={renderTooltip()}
                  style={{
                    display: 'flex',
                    justifyContent: templateOps.getElemenPosition(elementItem),
                    height: `${itemSelected.height}px`,
                    width: `${itemSelected.width}%`
                  }}
                >
                  <Card width="100%" height="100%">
                    <Styles.Iframe
                      key={`iframe-${itemSelected.url}`}
                      src={itemSelected.url}
                      height={`${itemSelected.height}px`}
                      width="100%"
                      title="External Page"
                    />
                  </Card>
                </Styles.LightTooltip>
              )}
            </Box>
          )}
        </>
      )}
      {isAdvancedMode && (
        <Box width="100%" id="main-box">
          <Box width="100%" id="box-component">
            {renderTable(elementsTableEdit)}
          </Box>
        </Box>
      )}
    </>
  );
};

TemplateMultipleItem.defaultProps = {
  updateAnnotationFields: () => console.error('no action to update')
};

TemplateMultipleItem.propTypes = {
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  isAdvancedMode: PropTypes.bool.isRequired,
  previewMode: PropTypes.string.isRequired,
  viewBasicEditMultis: PropTypes.bool.isRequired
};

export default TemplateMultipleItem;
